import React, { useState, useCallback } from 'react'
import axios from 'axios'
import { Button, Typography } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CompleteCycleDialog from './CompleteCycleDialog'
import { tokens } from './v4Styles'

const formatDate = (val) => {
  if (!val) return '\u2014'
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatTimeAgo = (date) => {
  if (!date) return ''
  const ms = Date.now() - new Date(date).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const categoryLabels = {
  needs_review: 'NEEDS REVIEW',
  aspire_tickets: 'ASPIRE TICKET',
  field_issues: 'FIELD ISSUE',
  stale: 'STALE',
  unread_messages: 'UNREAD MESSAGE'
}

const categoryColors = {
  needs_review: '#FF9800',
  aspire_tickets: '#7b1fa2',
  field_issues: '#c62828',
  stale: '#e65100',
  unread_messages: '#5d4037'
}

const NeedsReviewDetail = ({ data }) => (
  <>
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 12, color: '#333' }}>Spray Coverage</div>
      <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#333' }}>{data.scopeMode === 'all' ? 'All' : 'Selected'}</div>
          <div style={{ fontSize: 10, color: '#999' }}>Scope</div>
        </div>
        {data.totalAcres && (
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#333' }}>{data.totalAcres}</div>
            <div style={{ fontSize: 10, color: '#999' }}>Acres</div>
          </div>
        )}
      </div>
      <div style={{ fontSize: 10, color: '#999' }}>Submitted {formatTimeAgo(data.submittedAt)}</div>
    </div>
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Details</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
        <div><span style={{ color: '#999' }}>Last Applied:</span> <span style={{ color: '#333' }}>{formatDate(data.lastAppliedDate)}</span></div>
        <div><span style={{ color: '#999' }}>Next Scheduled:</span> <span style={{ color: '#333' }}>{formatDate(data.nextScheduledDate)}</span></div>
        <div><span style={{ color: '#999' }}>Contract:</span> <span style={{ color: data.hasHerbicideContract ? '#2e7d32' : '#e65100' }}>{data.hasHerbicideContract ? 'Recurring' : 'One-Time'}</span></div>
        <div><span style={{ color: '#999' }}>Blue Dye:</span> <span style={{ color: '#333' }}>{data.blueDyePreference || 'Unspecified'}</span></div>
      </div>
    </div>
  </>
)

const AspireTicketDetail = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Aspire Ticket</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
      <div><span style={{ color: '#999' }}>Ticket:</span> <span style={{ color: '#333', fontWeight: 600 }}>{data.workTicketNumber || '\u2014'}</span></div>
      <div><span style={{ color: '#999' }}>Status:</span> <span style={{ color: '#333' }}>{data.status || '\u2014'}</span></div>
      <div><span style={{ color: '#999' }}>Hours Actual:</span> <span style={{ color: '#333' }}>{data.hoursActual ?? '\u2014'}</span></div>
      <div><span style={{ color: '#999' }}>Hours Estimated:</span> <span style={{ color: '#333' }}>{data.hoursEstimated ?? '\u2014'}</span></div>
      <div><span style={{ color: '#999' }}>Scheduled:</span> <span style={{ color: '#333' }}>{formatDate(data.scheduledDate)}</span></div>
      <div><span style={{ color: '#999' }}>Last Visit:</span> <span style={{ color: '#333' }}>{formatDate(data.latestVisitDate)}</span></div>
      {data.crewLeaderName && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#999' }}>Crew Leader:</span> <span style={{ color: '#333' }}>{data.crewLeaderName}</span></div>}
    </div>
  </div>
)

const FieldIssueDetail = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Issue Report</div>
    <div style={{ fontSize: 13, color: '#333', marginBottom: 8, lineHeight: 1.5 }}>{data.issueText || 'No description'}</div>
    {data.photos?.length > 0 && (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {data.photos.map((url, i) => (
          <img key={i} src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
        ))}
      </div>
    )}
    <div style={{ fontSize: 10, color: '#999' }}>
      Reported by {data.reportedBy} &middot; {formatTimeAgo(data.reportedAt)}
    </div>
  </div>
)

const StaleDetail = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Stale Job</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
      <div><span style={{ color: '#999' }}>Queue Status:</span> <span style={{ color: '#e65100', fontWeight: 600 }}>{data.queueStatus === 'partial' ? 'Partial' : 'In Progress'}</span></div>
      <div><span style={{ color: '#999' }}>Days Stale:</span> <span style={{ color: '#c62828', fontWeight: 600 }}>{data.daysSinceActivity || '?'} days</span></div>
      <div><span style={{ color: '#999' }}>Last Activity:</span> <span style={{ color: '#333' }}>{formatDate(data.lastActivityDate)}</span></div>
    </div>
  </div>
)

const UnreadMessageDetail = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Message</div>
    <div style={{ fontSize: 13, color: '#333', marginBottom: 8, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
      {data.messageText || 'No message body'}
    </div>
    <div style={{ fontSize: 10, color: '#999' }}>
      From {data.senderName} &middot; {formatTimeAgo(data.sentAt)}
    </div>
  </div>
)

const detailRenderers = {
  needs_review: NeedsReviewDetail,
  aspire_tickets: AspireTicketDetail,
  field_issues: FieldIssueDetail,
  stale: StaleDetail,
  unread_messages: UnreadMessageDetail
}

const ReviewDetailPanel = ({ item, url, onViewInQueue, onItemCompleted }) => {
  const [completeCycleOpen, setCompleteCycleOpen] = useState(false)
  const [completeCycleLoading, setCompleteCycleLoading] = useState(false)
  const [completeCycleError, setCompleteCycleError] = useState('')

  const handleCompleteCycle = useCallback(async (scopeAction) => {
    if (!item) return
    setCompleteCycleLoading(true)
    setCompleteCycleError('')
    try {
      await axios.put(`${url}/api/herbicide-schedule/${item.scheduleItemId}/complete-cycle`, { scopeAction })
      setCompleteCycleOpen(false)
      if (onItemCompleted) onItemCompleted(item)
    } catch (err) {
      console.error('Error completing cycle:', err)
      setCompleteCycleError(err.response?.data?.error || 'Failed to complete cycle.')
    }
    setCompleteCycleLoading(false)
  }, [item, url, onItemCompleted])

  if (!item) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.text.muted }}>
        <Typography variant="body2">Select an item to view details</Typography>
      </div>
    )
  }

  const DetailRenderer = detailRenderers[item.category]
  const color = categoryColors[item.category] || '#666'

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: '#fafbfc' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ background: color, color: '#fff', fontSize: 9, padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>
          {categoryLabels[item.category]}
        </span>
        <span
          style={{ fontSize: 11, color: tokens.accent.blue, cursor: 'pointer', textDecoration: 'underline', marginLeft: 'auto' }}
          onClick={() => onViewInQueue(item.scheduleItemId)}
        >
          View in Queue &rarr;
        </span>
      </div>
      <Typography variant="h6" style={{ margin: '0 0 4px', fontSize: 18, color: tokens.text.primary }}>{item.siteName}</Typography>
      <Typography variant="body2" style={{ margin: '0 0 20px', color: tokens.text.secondary, fontSize: 12 }}>{item.address}</Typography>

      {DetailRenderer && <DetailRenderer data={item.data} />}

      <div style={{ display: 'flex', gap: 10 }}>
        {item.category === 'needs_review' && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={() => setCompleteCycleOpen(true)}
          >
            Complete Cycle
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={() => onViewInQueue(item.scheduleItemId)}
        >
          View in Queue
        </Button>
      </div>

      <CompleteCycleDialog
        open={completeCycleOpen}
        loading={completeCycleLoading}
        error={completeCycleError}
        onClose={() => setCompleteCycleOpen(false)}
        onComplete={handleCompleteCycle}
      />
    </div>
  )
}

export default ReviewDetailPanel
